import styled from '@emotion/styled';
import { DSButton } from '@hundred5/design-system';
import React, { useCallback, useEffect, useState } from 'react';
import { useMedia } from 'react-use';

import { OnboardingModalContext } from '../../contexts/onboarding-context';
import { ONBOARDING_STEPS } from '../../utils/onboarding-steps';

export function OnboardingTooltip() {
  const { type, step, skipOnboarding, nextStep, startOnboarding } =
    React.useContext(OnboardingModalContext);
  const isMobile = useMedia('(max-width: 768px)');
  const isLastStep = step === 4;
  const [currentElement, setCurrentElement] = useState<Element | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Start onboarding with first step when component mounts
  useEffect(() => {
    if (!type && !isMobile) {
      // Function to check if onboarding elements are ready
      let retryCount = 0;
      const maxRetries = 10;

      const checkOnboardingElements = () => {
        const appliedElement = document.querySelector(
          '[data-rh-onboarding="applied"]'
        );

        if (appliedElement) {
          startOnboarding();
        } else if (retryCount < maxRetries) {
          retryCount++;

          setTimeout(checkOnboardingElements, 1000);
        }
      };

      // Start checking after initial delay
      const timer = setTimeout(checkOnboardingElements, 1000);
      return () => clearTimeout(timer);
    }
  }, [type, isMobile, startOnboarding]);

  const calculateTooltipPosition = useCallback(
    (rect: DOMRect, position: string) => {
      const tooltipWidth = 325;
      const tooltipHeight = 138;
      const offset = 10;

      switch (position) {
        case 'top':
          return {
            x: rect.left + rect.width / 2 - tooltipWidth / 2,
            y: rect.top - tooltipHeight - offset,
          };
        case 'bottom':
          return {
            x:
              rect.left +
              rect.width / 2 -
              tooltipWidth / 2 +
              (step === 1 ? 36 : 0), // add 36 only for the first step, the width of the recommended column it is not render due to split treatment
            y: rect.bottom + offset,
          };
        case 'left':
          return {
            x: rect.left - tooltipWidth - offset,
            y: rect.top + rect.height / 2 - tooltipHeight / 2,
          };
        case 'right':
          return {
            x: rect.right + offset,
            y: rect.top + rect.height / 2 - tooltipHeight / 2,
          };
        default:
          return {
            x: rect.left + rect.width / 2 - tooltipWidth / 2,
            y: rect.bottom + offset,
          };
      }
    },
    [step]
  );

  // Show tooltip for the current step
  useEffect(() => {
    if (type) {
      const currentStep = ONBOARDING_STEPS[type];
      if (currentStep) {
        const element = document.querySelector(currentStep.elementSelector);

        // If element doesn't exist, try again after a short delay
        if (!element) {
          const timer = setTimeout(() => {
            const retryElement = document.querySelector(
              currentStep.elementSelector
            );

            if (retryElement) {
              setCurrentElement(retryElement);
              const rect = retryElement.getBoundingClientRect();
              const position = calculateTooltipPosition(
                rect,
                currentStep.position
              );
              setTooltipPosition(position);
            }
          }, 500);
          return () => clearTimeout(timer);
        }

        if (element) {
          setCurrentElement(element);
          // Calculate tooltip position
          const rect = element.getBoundingClientRect();
          const position = calculateTooltipPosition(rect, currentStep.position);
          setTooltipPosition(position);
        }
      }
    } else {
      setCurrentElement(null);
      setTooltipPosition(null);
    }
  }, [type, calculateTooltipPosition]);

  if (!currentElement || !tooltipPosition || !type) {
    return null;
  }

  const currentStep = ONBOARDING_STEPS[type];

  return (
    <TooltipOverlay>
      <TooltipContent
        style={{
          left: tooltipPosition.x,
          top: tooltipPosition.y,
        }}
        position={currentStep?.position || 'bottom'}
      >
        <Title>{currentStep?.title}</Title>
        <Content>{currentStep?.content}</Content>
        <Footer>
          <DSButton
            size="small"
            variant="secondary"
            onClick={() => {
              skipOnboarding(type);
            }}
          >
            Skip
          </DSButton>
          <DSButton
            size="small"
            variant="primary"
            onClick={() => nextStep(type)}
          >
            {isLastStep ? 'Finish' : 'Next'}
          </DSButton>
        </Footer>
      </TooltipContent>
    </TooltipOverlay>
  );
}

const TooltipOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  pointer-events: none;
  background-color: rgba(44, 19, 56, 0.2);
  -webkit-transition: opacity 200ms;
  transition: opacity 200ms;
  opacity: 1;
  overflow-y: unset;
  -webkit-animation: animate-opacity 200ms;
  animation: animate-opacity 200ms;
`;

const TooltipContent = styled.div<{ position: string }>`
  position: absolute;
  padding: 16px;
  font-size: 14px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.white};
  color: ${(props) => props.theme.typography.colorPrimary};
  box-shadow: ${(props) => props.theme.shadow.popup};
  width: max-content;
  max-width: 325px;
  pointer-events: auto;

  &::after {
    content: '';
    position: absolute;
    width: 12px;
    height: 0;
    border: 12px solid transparent;
    z-index: 1;
  }

  ${({ position }) => {
    switch (position) {
      case 'top':
        return `
          &::after {
            bottom: -5px;
            left: calc(50% - 6px); // TOOLTIP_ARROW_HORIZONTAL_OFFSET
            transform: rotate(45deg);
            border-top-color: white;
            border-bottom: none;
            border-left: none;
            border-right: none;
          }
        `;
      case 'bottom':
        return `
          &::after {
            top: -5px;
            left: calc(25% + 6px);  // TOOLTIP_ARROW_HORIZONTAL_OFFSET
            transform: rotate(45deg);
            border-bottom-color: white;
            border-top: none;
            border-left: none;
            border-right: none;
          }
        `;
      case 'left':
        return `
          &::after {
            height: 12px;
            right: -5px;
            top: 30%;  // TOOLTIP_ARROW_HORIZONTAL_OFFSET
            transform: rotate(45deg);
            border-left-color: white;
            border-right: none;
            border-top: none;
            border-bottom: none;
          }
        `;
      case 'right':
        return `
          &::after {
            height: 12px;
            left: -5px;
            top: calc(50% - 12px);  // TOOLTIP_ARROW_HORIZONTAL_OFFSET
            transform: rotate(45deg);
            border-right-color: white;
            border-left: none;
            border-top: none;
            border-bottom: none;
          }
        `;
      default:
        return `
          &::after {
            top: -24px;
            left: 50%;
            transform: translateX(-50%);
            border-bottom-color: white;
            border-top: none;
            border-left: none;
            border-right: none;
          }
        `;
    }
  }}
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: ${(props) => props.theme.typography.colorPrimary};
`;

const Content = styled.div`
  font-size: 12px;
  line-height: 1.4;
  color: ${(props) => props.theme.typography.colorSecondary};
`;

const Footer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 12px;
`;
